import type { InboxAPI } from "@customerio/cdp-analytics-browser";
import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";

import { AnalyticsEvents, AnalyticsTraits, IAnalyticsAdapter } from "../types";

type InAppMessageEvent = {
  type: string;
  data?: {
    deliveryId?: string;
    messageId?: string;
    actionName?: string;
    actionValue?: string;
    errorMessage?: string;
  };
};

type PendingIdentify = {
  userId: string;
  traits: AnalyticsTraits;
};

export class CustomerIoAdapter implements IAnalyticsAdapter {
  public cioanalytics?: AnalyticsBrowser;
  private readonly writeKey?: string;
  private readonly siteId?: string;
  private readonly onMessageAction?: (actionName: string, actionValue: string) => void;
  private readonly segmentTrack?: (eventName: string, properties?: Record<string, unknown>) => void;
  private pendingIdentify?: PendingIdentify;
  private readyPromise?: Promise<void>;

  private identifiedUserId?: string;
  private identifiedResolvers: Array<(userId: string) => void> = [];
  private identifyListeners: Array<(userId: string) => void> = [];

  constructor(
    writeKey?: string,
    siteId?: string,
    options?: {
      onMessageAction?: (actionName: string, actionValue: string) => void;
      segmentTrack?: (eventName: string, properties?: Record<string, unknown>) => void;
    },
  ) {
    this.writeKey = writeKey;
    this.siteId = siteId;
    this.onMessageAction = options?.onMessageAction;
    this.segmentTrack = options?.segmentTrack;
  }

  initialize(): void {
    if (!this.writeKey || !this.siteId) {
      console.warn(
        "REACT_APP_CUSTOMERIO_WRITE_KEY or REACT_APP_CUSTOMERIO_SITE_ID variable not provided; customer.io is not initialized (in-app messaging and inbox are disabled).",
      );
      return;
    }

    this.cioanalytics = AnalyticsBrowser.load(
      { writeKey: this.writeKey },
      {
        integrations: {
          "Customer.io In-App Plugin": {
            siteId: this.siteId,
            // Admin users are always authenticated; anonymous in-app messages
            // would be a misfire in an employee portal.
            anonymousInApp: false,
            events: ((event: InAppMessageEvent) => {
              this.handleInAppMessageEvent(event);
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any,
          },
        },
      },
    );

    // Capture a promise that resolves once the plugin pipeline is ready. All
    // mutating public methods await this before forwarding to the SDK so that
    // calls made very early in page load do not race the plugin load and get
    // silently dropped.
    this.readyPromise = new Promise<void>((resolve) => {
      this.cioanalytics?.ready(() => {
        resolve();
      });
    });

    if (import.meta.env.DEV) {
      console.log("Customer.io adapter initialized");
    }
  }

  trackEvent<Event extends keyof AnalyticsEvents>(
    _event: Event,
    _properties: AnalyticsEvents[Event],
  ): void {
    // Customer.io events are emitted by the SDK integration callback.
  }

  trackPage(name: string): void {
    void this.whenReady(() => {
      this.cioanalytics?.page(name);
    });
  }

  identify(userId: string, traits: AnalyticsTraits): void {
    // Buffer the latest identify args so that if a later call arrives before
    // ready(), only the most recent one is applied.
    this.pendingIdentify = { userId, traits };
    void this.whenReady(() => {
      const pending = this.pendingIdentify;
      if (!pending) {
        return;
      }
      this.cioanalytics?.identify(pending.userId, pending.traits);

      // Registering the platform as a CIO "group" mirrors the backend's
      // SegmentNotificationServiceAdapter.IdentifyPersonWithPlatformContext
      // behavior and lets CIO campaigns segment on groupId.
      if (pending.traits.currentPlatformId) {
        this.cioanalytics?.group(pending.traits.currentPlatformId, {
          name: pending.traits.currentPlatformName ?? undefined,
          domain: pending.traits.currentPlatformDomain ?? undefined,
        });
      }

      this.markIdentified(pending.userId);

      if (import.meta.env.DEV) {
        console.log("Customer.io identify dispatched for", pending.userId);
      }
    });
  }

  private markIdentified(userId: string): void {
    this.identifiedUserId = userId;
    const resolvers = this.identifiedResolvers;
    this.identifiedResolvers = [];
    for (const resolve of resolvers) {
      resolve(userId);
    }
    for (const listener of this.identifyListeners) {
      try {
        listener(userId);
      } catch (error) {
        console.warn("Customer.io identify listener threw", error);
      }
    }
  }

  /**
   * Returns the user id the Customer.io SDK is currently identified as. The
   * inbox subscription in `useCioInbox` uses this to re-subscribe whenever the
   * identified user changes (e.g. after login, after switching between
   * multi-platform admins). Returns `undefined` when identify has not yet
   * flushed.
   */
  getIdentifiedUserId(): string | undefined {
    return this.identifiedUserId;
  }

  /**
   * Wait for the SDK to be identified. Resolves immediately if it already is.
   * Returns `undefined` when the SDK was never initialized.
   */
  waitForIdentifiedUserId(): Promise<string | undefined> {
    if (!this.cioanalytics) return Promise.resolve(undefined);
    if (this.identifiedUserId) return Promise.resolve(this.identifiedUserId);
    return new Promise<string>((resolve) => {
      this.identifiedResolvers.push(resolve);
    });
  }

  /**
   * Subscribe to identify changes. The listener fires every time the
   * identified user id changes (including the first successful identify).
   * Returns an unsubscribe function.
   */
  onIdentifyChanged(listener: (userId: string) => void): () => void {
    this.identifyListeners.push(listener);
    if (this.identifiedUserId) {
      try {
        listener(this.identifiedUserId);
      } catch (error) {
        console.warn("Customer.io identify listener threw", error);
      }
    }
    return () => {
      this.identifyListeners = this.identifyListeners.filter((l) => l !== listener);
    };
  }

  reset(): void {
    this.pendingIdentify = undefined;
    this.identifiedUserId = undefined;
    void this.whenReady(() => {
      this.cioanalytics?.reset();
      if (import.meta.env.DEV) {
        console.log("Customer.io identity reset");
      }
    });
  }

  /**
   * Returns the underlying AnalyticsBrowser instance once it is safe to use
   * (after `ready()`), or `undefined` if the SDK was never initialized (e.g.
   * missing env vars in local dev).
   *
   * The return type intentionally wraps the browser in a `{ analytics }`
   * object so that awaiting does NOT unwrap AnalyticsBrowser's own
   * PromiseLike<[Analytics, Context]> conformance — that unwrap drops
   * AnalyticsBuffered's `inbox()` method from the type surface.
   */
  async getReadyAnalyticsBrowser(): Promise<{ readonly analytics: AnalyticsBrowser } | undefined> {
    if (!this.cioanalytics) {
      return undefined;
    }
    await this.readyPromise;
    return { analytics: this.cioanalytics };
  }

  /**
   * Returns the Customer.io notification inbox API once the SDK + In-App
   * Plugin are loaded and the user is identified. Returns `null` when the
   * SDK was never initialized or when the In-App Plugin did not expose the
   * inbox method (e.g. Notification Inbox is disabled in the CIO workspace).
   */
  async getReadyInbox(topics: readonly string[] = []): Promise<InboxAPI | null> {
    const ready = await this.getReadyAnalyticsBrowser();
    if (!ready) return null;
    const inboxFactory = ready.analytics.inbox?.bind(ready.analytics);
    if (typeof inboxFactory !== "function") return null;
    return inboxFactory(...topics);
  }

  private async whenReady(callback: () => void): Promise<void> {
    if (!this.readyPromise) {
      return;
    }
    await this.readyPromise;
    callback();
  }

  private handleInAppMessageEvent(event: InAppMessageEvent): void {
    const { type, data } = event;

    const eventName = type.replace("in-app:", "Customer.io ");
    const eventProperties = {
      deliveryId: data?.deliveryId,
      messageId: data?.messageId,
      actionName: data?.actionName,
      actionValue: data?.actionValue,
      errorMessage: data?.errorMessage,
    };

    this.segmentTrack?.(eventName, eventProperties);

    if (type === "in-app:message-action" && data?.actionName && data.actionValue) {
      this.onMessageAction?.(data.actionName, data.actionValue);
    }
  }
}
